import { generatePreviewCode } from '@/components/actions/program/DiscountCodePatternBuilder';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import { usePreviewBrandName } from '@/components/actions/program/use-preview-brand-name';
import { useInvitationActions, useInvitationData } from '@/contexts/invitation-context';
import { useCreateProgramInvitation, useCreateProgramInvitationByEmail } from '@/hooks/actions';
import { apiErrorMessage } from '@/libs/api-error';
import type { InvitableProgramResponse } from '@/types/affiliate-programs';
import {
  DEFAULT_CODE_PATTERN,
  DISCOUNT_CODE_RULES,
  type CreateInvitationResponse,
} from '@fluenceur/types';
import { Box, Button, Code, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NoInvitableProgramsMessage from './no-invitable-programs-message';
import ProgramSelection from './program-selection';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';

interface ProgramSelectionViewProps {
  onClose: () => void;
}

const ProgramSelectionView: React.FC<ProgramSelectionViewProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    programs,
    selectedProgram,
    creator,
    target,
    sourceRecommendationId,
    recipientEmail,
    collabRecipientIdentityId,
    entryIntent,
    affiliateAddDraft,
  } = useInvitationData();

  const { setSelectedProgram, setInvitationData, setAffiliateAddDraft } = useInvitationActions();
  const brandName = usePreviewBrandName();
  // A pristine form is not an invalid one: hold the required-field error back
  // until the brand has actually engaged with the input.
  const [isFirstNameTouched, setIsFirstNameTouched] = useState(false);

  const { mutate: handleCreateInvitation, isPending: isCreatingInvitation } =
    useCreateProgramInvitation();
  const { mutate: handleCreateInvitationByEmail, isPending: isCreatingByEmail } =
    useCreateProgramInvitationByEmail();

  const handleProgramSelect = useCallback(
    (program: Partial<InvitableProgramResponse>) => {
      setSelectedProgram(program);
    },
    [setSelectedProgram]
  );

  // Check if all programs are disabled
  const allProgramsDisabled = programs.every((program) => !program.canInvite);
  const trimmedFirstName = affiliateAddDraft.firstName.trim();
  const trimmedDiscountCode = affiliateAddDraft.discountCode.trim();
  const isDiscountEnabled = selectedProgram.discountSettings?.enabled ?? false;
  const discountCodePreview = useMemo(() => {
    if (!isDiscountEnabled || !selectedProgram.name) return null;
    return generatePreviewCode(
      selectedProgram.discountSettings?.codePattern ?? DEFAULT_CODE_PATTERN,
      {
        brandName,
        discountValue: selectedProgram.discountSettings?.value ?? 10,
      },
      trimmedFirstName || undefined
    );
  }, [
    brandName,
    isDiscountEnabled,
    selectedProgram.discountSettings,
    selectedProgram.name,
    trimmedFirstName,
  ]);
  const firstNameError = useMemo(() => {
    if (entryIntent !== 'affiliate-add') return null;
    if (!trimmedFirstName) return t('affiliates.addNew.validation.firstNameRequired');
    if (trimmedFirstName.length > 255) return t('affiliates.addNew.validation.firstNameTooLong');
    return null;
  }, [entryIntent, t, trimmedFirstName]);
  const discountCodeError = useMemo(() => {
    if (entryIntent !== 'affiliate-add' || !trimmedDiscountCode) return null;
    if (trimmedDiscountCode.length < DISCOUNT_CODE_RULES.minLength)
      return t('affiliates.addNew.validation.discountCodeMinLength');
    if (trimmedDiscountCode.length > DISCOUNT_CODE_RULES.maxLength)
      return t('affiliates.addNew.validation.discountCodeMaxLength');
    if (!DISCOUNT_CODE_RULES.pattern.test(trimmedDiscountCode))
      return t('affiliates.addNew.validation.discountCodeInvalidChars');
    return null;
  }, [entryIntent, t, trimmedDiscountCode]);
  const visibleFirstNameError = isFirstNameTouched ? firstNameError : null;
  // The affiliate row already carries the identity; no need to mirror it in the draft.
  const affiliateUserId = target?.type === 'affiliate' ? target.data.id : null;

  const handleCreate = useCallback(async () => {
    if (!selectedProgram) return;

    // Both creation paths land on the same seeded invitationData: a real
    // invitationId (the outreach send requires it) and a real invitationLink
    // (the universal floor the compose step displays).
    const seedCreatedInvitation = (createdInvitation: CreateInvitationResponse) => {
      // Prefix key: refreshes both the invitation lists and the stats query the
      // sidebar badge reads. Unconditional because the invitation already
      // exists at this step, whatever the brand does with the later steps.
      queryClient.invalidateQueries({ queryKey: ['program-invitations'] });
      // setInvitationData transitions to the channel step (mono) or straight to
      // the details view (bulk).
      setInvitationData({
        programName: selectedProgram.name,
        loading: false,
        error: false,
        socialProfileId: createdInvitation.socialProfileId,
        invitationId: createdInvitation.invitationId,
        invitationLink: createdInvitation.invitationLink,
        instagramDmLink: createdInvitation.instagramDmLink,
        sourceRecommendationId: createdInvitation.sourceRecommendationId ?? sourceRecommendationId,
      });
    };

    // One error surface for every creation path: the backend's user-facing
    // message as-is (e.g. a suppressed recipient), never swallowed.
    const onCreateError = (error: unknown) => {
      // A plan refusal opens the upsell from the API client; a toast would repeat it.
      if (isPlanLimitError(error)) return;
      toaster.create({
        title: apiErrorMessage(error, 'Error creating invitation'),
        type: 'error',
        duration: 3000,
      });
    };

    // Shared by both email-shaped creation paths below.
    const affiliateAddPayload =
      entryIntent === 'affiliate-add'
        ? {
            intent: 'affiliate_add' as const,
            firstName: trimmedFirstName,
            ...(affiliateUserId ? { userId: affiliateUserId } : {}),
            ...(isDiscountEnabled && trimmedDiscountCode
              ? { discountCode: trimmedDiscountCode }
              : {}),
          }
        : {};

    // Email target (email-only contact or raw email): create the invitation
    // UPFRONT by email — idempotent per (email, program) — so the compose step
    // can both display the link and actually send.
    if (target?.type === 'email') {
      handleCreateInvitationByEmail(
        {
          programId: selectedProgram.id as string,
          email: target.data.email,
          creatorIdentityId: collabRecipientIdentityId ?? undefined,
          ...affiliateAddPayload,
        },
        { onSuccess: seedCreatedInvitation, onError: onCreateError }
      );
      return;
    }

    // An affiliate picked in the smart-input flow always carries an email:
    // create upfront by email too. Falling through to the creator path would
    // dereference a null `creator`.
    if (target?.type === 'affiliate') {
      handleCreateInvitationByEmail(
        {
          programId: selectedProgram.id as string,
          email: target.data.email,
          ...affiliateAddPayload,
        },
        { onSuccess: seedCreatedInvitation, onError: onCreateError }
      );
      return;
    }

    // Bulk wishlist outreach keeps its deferred creation: the invitations are
    // created per recipient when the user submits the batch. setInvitationData
    // routes to the channel step (link/email), matching the mono flow.
    if (target?.type === 'wishlist-bulk') {
      setInvitationData({
        programName: selectedProgram.name,
        loading: false,
        error: false,
      } as any); // Cast to any since we're not creating a full invitation yet
      return;
    }

    // For creator-based invitations, create the invitation first
    handleCreateInvitation(
      {
        creatorId: creator!.id,
        programId: selectedProgram.id as string,
        recommendationId: sourceRecommendationId,
        recipientEmail: recipientEmail ?? undefined,
      },
      { onSuccess: seedCreatedInvitation, onError: onCreateError }
    );
  }, [
    selectedProgram,
    handleCreateInvitation,
    handleCreateInvitationByEmail,
    target,
    collabRecipientIdentityId,
    creator,
    sourceRecommendationId,
    recipientEmail,
    queryClient,
    setInvitationData,
    entryIntent,
    trimmedFirstName,
    affiliateUserId,
    trimmedDiscountCode,
    isDiscountEnabled,
  ]);

  const hasPrograms = programs && programs.length > 0;

  return (
    <VStack w="full" align="stretch" gap={6} position="relative">
      <ProgramSelection
        programs={programs || []}
        selected={selectedProgram}
        setSelectedProgram={handleProgramSelect}
      />
      {entryIntent === 'affiliate-add' && (
        <>
          <Field
            label={t('affiliates.addNew.firstNameLabel')}
            required
            invalid={Boolean(visibleFirstNameError)}
            errorText={visibleFirstNameError}
          >
            <Input
              variant="outline"
              value={affiliateAddDraft.firstName}
              onChange={(event) => {
                setIsFirstNameTouched(true);
                setAffiliateAddDraft({
                  ...affiliateAddDraft,
                  firstName: event.target.value,
                });
              }}
              onBlur={() => setIsFirstNameTouched(true)}
              placeholder={t('affiliates.addNew.firstNamePlaceholder')}
              disabled={Boolean(affiliateUserId)}
              readOnly={Boolean(affiliateUserId)}
            />
          </Field>
          {isDiscountEnabled && (
            <>
              <Field
                label={t('affiliates.addNew.discountCodeLabel')}
                invalid={Boolean(discountCodeError)}
                errorText={discountCodeError}
                helperText={t('affiliates.addNew.discountCodeHelper')}
              >
                <Input
                  variant="outline"
                  value={affiliateAddDraft.discountCode}
                  onChange={(event) =>
                    setAffiliateAddDraft({
                      ...affiliateAddDraft,
                      discountCode: event.target.value,
                    })
                  }
                  placeholder={t('affiliates.addNew.discountCodePlaceholder')}
                />
              </Field>
              {!trimmedDiscountCode && discountCodePreview && (
                <Text fontSize="sm" color="fg.muted">
                  {t('affiliates.addNew.autoGeneratedCode')}{' '}
                  <Code fontSize="sm" fontFamily="mono" fontWeight="semibold">
                    {discountCodePreview}
                  </Code>
                </Text>
              )}
            </>
          )}
        </>
      )}
      {/* Show message if all programs are disabled */}
      {allProgramsDisabled && programs.length > 0 && (
        <Box mt={4}>
          <NoInvitableProgramsMessage programs={programs} />
        </Box>
      )}
      {!hasPrograms && (
        <Text fontSize="sm" color="orange.500" mt={4}>
          {t('invitations.modal.programSelection.noPrograms')}
        </Text>
      )}

      <HStack w="full" justify="flex-end" gap={4}>
        <Button variant="ghost" onClick={onClose}>
          {t('invitations.modal.programSelection.cancel')}
        </Button>
        <Button
          colorPalette="primary"
          onClick={handleCreate}
          loading={isCreatingInvitation || isCreatingByEmail}
          disabled={
            !selectedProgram?.id ||
            !hasPrograms ||
            isCreatingInvitation ||
            isCreatingByEmail ||
            allProgramsDisabled ||
            Boolean(firstNameError) ||
            Boolean(isDiscountEnabled && discountCodeError)
          }
        >
          {t('invitations.modal.programSelection.create')}
        </Button>
      </HStack>
    </VStack>
  );
};

ProgramSelectionView.displayName = 'ProgramSelectionView';

export default ProgramSelectionView;
